/**
 * TUS Protocol Upload Utility
 * Implements TUS resumable upload protocol for large files
 */

import { getSttClient } from '../api/axiosClients';
import { getMaxFileSize, getUploadTimeout } from '../config';

const TUS_VERSION = '1.0.0';
const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

/**
 * Upload file using TUS protocol (via tusd)
 * @param {File} file - File to upload
 * @param {string} uploadToken - Upload token from /api/tusd/upload-token endpoint
 * @param {Function} onProgress - Progress callback (percent, uploaded, total)
 * @param {string} apiKeyValue - API key value for tusd auth
 * @param {number} chunkSize - Chunk size in bytes
 * @returns {Promise<string>} - TUS file ID
 */
export const uploadWithTUS = async (file, uploadToken, onProgress = null, chunkSize = DEFAULT_CHUNK_SIZE, apiKeyValue = null) => {
    // TUS endpoints are at /api/tusd (routed to tusd via traefik)
    const baseURL = '/api/tusd';
    const apiKey = apiKeyValue || null;
    
    // Get upload timeout from config (default 2 minutes, but can be longer for large files)
    const uploadTimeout = getUploadTimeout();
    
    // Helper function to create fetch with timeout
    const fetchWithTimeout = async (url, options = {}) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), uploadTimeout);
        
        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                throw new Error(`Request timed out after ${uploadTimeout / 1000} seconds`);
            }
            throw error;
        }
    };
    
    // 1. Check server capabilities (OPTIONS)
    try {
        const optionsHeaders = apiKey ? { 'X-API-Key': apiKey } : {};
        const optionsResponse = await fetchWithTimeout(`${baseURL}/files`, {
            method: 'OPTIONS',
            headers: optionsHeaders
        });
        
        if (!optionsResponse.ok) {
            throw new Error('TUS server not available');
        }
    } catch (error) {
        throw new Error(`TUS protocol not supported: ${error.message}`);
    }
    
    // 2. Create upload (POST) with metadata
    // SECURITY: Only send upload_token and filename - user_id/api_key_id come from server-side token lookup
    
    // Helper function to encode Unicode strings to base64
    // btoa() only works with Latin1 characters, so we need to convert Unicode to UTF-8 first
    const base64Encode = (str) => {
        return btoa(unescape(encodeURIComponent(str)));
    };
    
    const filename = base64Encode(file.name);
    const uploadTokenEncoded = btoa(uploadToken); // UUID should be ASCII-safe
    
    // Build Upload-Metadata header (comma-separated key-value pairs)
    const uploadMetadata = [
        `filename ${filename}`,
        `upload_token ${uploadTokenEncoded}`
    ].join(',');
    
    const headers = {
        'Tus-Resumable': TUS_VERSION,
        'Upload-Length': file.size.toString(),
        'Upload-Metadata': uploadMetadata,
    };
    if (apiKey) {
        headers['X-API-Key'] = apiKey;
    }
    
    const createResponse = await fetchWithTimeout(`${baseURL}/files`, {
        method: 'POST',
        headers: headers,
    });
    
    if (!createResponse.ok) {
        const errorText = await createResponse.text();
        throw new Error(`Failed to create TUS upload: ${createResponse.status} ${errorText}`);
    }
    
    const location = createResponse.headers.get('Location');
    if (!location) {
        throw new Error('No Location header in TUS create response');
    }
    
    const fileId = location.split('/').pop();
    const uploadURL = `${baseURL}/files/${fileId}`;
    
    // 3. Upload file in chunks (PATCH)
    let offset = 0;
    let uploadedBytes = 0;
    
    while (offset < file.size) {
        const chunk = file.slice(offset, offset + chunkSize);
        const chunkData = await chunk.arrayBuffer();
        
        // Retry logic for chunk upload
        let retries = 3;
        let chunkUploaded = false;
        
        while (retries > 0 && !chunkUploaded) {
            try {
                // Check current offset (HEAD)
                const headHeaders = { 'Tus-Resumable': TUS_VERSION };
                if (apiKey) {
                    headHeaders['X-API-Key'] = apiKey;
                }
                const headResponse = await fetchWithTimeout(uploadURL, {
                    method: 'HEAD',
                    headers: headHeaders,
                });
                
                if (headResponse.ok) {
                    const currentOffset = parseInt(headResponse.headers.get('Upload-Offset') || '0', 10);
                    if (currentOffset !== offset) {
                        offset = currentOffset;
                        uploadedBytes = currentOffset;
                        if (onProgress) {
                            onProgress({
                                percent: Math.round((uploadedBytes / file.size) * 100),
                                uploaded: uploadedBytes,
                                total: file.size,
                            });
                        }
                        // Continue from current offset
                        continue;
                    }
                }
                
                // Upload chunk (PATCH)
                const patchHeaders = {
                    'Tus-Resumable': TUS_VERSION,
                    'Upload-Offset': offset.toString(),
                    'Content-Type': 'application/offset+octet-stream',
                };
                if (apiKey) {
                    patchHeaders['X-API-Key'] = apiKey;
                }
                const patchResponse = await fetchWithTimeout(uploadURL, {
                    method: 'PATCH',
                    headers: patchHeaders,
                    body: chunkData,
                });
                
                if (!patchResponse.ok) {
                    if (patchResponse.status === 409) {
                        // Offset mismatch - get current offset and retry
                        const headRetryHeaders = { 'Tus-Resumable': TUS_VERSION };
                        if (apiKey) {
                            headRetryHeaders['X-API-Key'] = apiKey;
                        }
                        const headRetryResponse = await fetchWithTimeout(uploadURL, {
                            method: 'HEAD',
                            headers: headRetryHeaders,
                        });
                        if (headRetryResponse.ok) {
                            offset = parseInt(headRetryResponse.headers.get('Upload-Offset') || '0', 10);
                            uploadedBytes = offset;
                            retries--;
                            continue;
                        }
                    }
                    throw new Error(`Chunk upload failed: ${patchResponse.status}`);
                }
                
                const newOffset = parseInt(patchResponse.headers.get('Upload-Offset') || '0', 10);
                offset = newOffset;
                uploadedBytes = newOffset;
                chunkUploaded = true;
                
                if (onProgress) {
                    onProgress({
                        percent: Math.round((uploadedBytes / file.size) * 100),
                        uploaded: uploadedBytes,
                        total: file.size,
                    });
                }
            } catch (error) {
                retries--;
                if (retries === 0) {
                    throw new Error(`Failed to upload chunk after retries: ${error.message}`);
                }
                // Wait before retry (exponential backoff)
                await new Promise(resolve => setTimeout(resolve, 1000 * (4 - retries)));
            }
        }
        
        if (!chunkUploaded) {
            throw new Error('Failed to upload chunk');
        }
    }
    
    // 4. Verify upload is complete
    const finalHeadHeaders = { 'Tus-Resumable': TUS_VERSION };
    if (apiKey) {
        finalHeadHeaders['X-API-Key'] = apiKey;
    }
    const finalHeadResponse = await fetchWithTimeout(uploadURL, {
        method: 'HEAD',
        headers: finalHeadHeaders,
    });
    
    if (!finalHeadResponse.ok) {
        throw new Error('Failed to verify upload completion');
    }
    
    const finalOffset = parseInt(finalHeadResponse.headers.get('Upload-Offset') || '0', 10);
    const uploadLength = parseInt(finalHeadResponse.headers.get('Upload-Length') || '0', 10);
    
    if (finalOffset !== uploadLength) {
        throw new Error(`Upload incomplete: ${finalOffset}/${uploadLength} bytes`);
    }
    
    return fileId;
};

